import "./EditDealDatesPanel.css";

import { hasDealDateValidationError } from "../../utils";
import { Button, Column, DatePicker, Divider, ErrorBox, FlyoutPanel, KanaTooltip, Row } from "../../widget";
import { EditDealDatesPanelProps } from "./models/EditDealDatesPanel";
import { useEditDealDatesPanel } from "./useEditDealDatesPanel";

export const EditDealDatesPanel = ({
  deal,
  workflowTemplatePhases,
  show,
  onClose,
  onSaved,
}: EditDealDatesPanelProps): JSX.Element => {
  const { errors, errorFieldKeys, isSaving, sections, handleClose, handleDateChange, handleSubmit } =
    useEditDealDatesPanel({
      deal,
      workflowTemplatePhases,
      show,
      onClose,
      onSaved,
    });

  const content = (
    <div className="EditDealDatesPanelContent">
      {errors && errors.length > 0 && (
        <Row spacingV="ml" justify="between">
          <Column span={12}>
            <ErrorBox text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}

      <div className="EditDealDatesPanelIntro">
        <h2>Stage dates</h2>
      </div>

      <form onSubmit={handleSubmit}>
        {sections.map((section, index) => (
          <div key={section.key} className="EditDealDatesPanelSection">
            <div className="EditDealDatesPanelSectionHeader">
              <p className="body01">{section.title}</p>
              {section.description && (
                <p className="body1 EditDealDatesPanelSectionDescription">{section.description}</p>
              )}
            </div>
            <Row spacingV="ll" className="EditDealDatesPanelFields">
              <Column span="4_half">
                <DatePicker
                  value={section.start}
                  onChange={(value) => handleDateChange(section.key, "start", value)}
                  label="Stage start date"
                  corners="rounded"
                  hasError={hasDealDateValidationError(errorFieldKeys, section.key, "start")}
                  tooltip={<KanaTooltip tooltipHeader="" tooltipText="Select the date this stage starts." />}
                />
              </Column>
              <Column span="4_half" offset={1}>
                <DatePicker
                  value={section.end}
                  onChange={(value) => handleDateChange(section.key, "end", value)}
                  label="Stage end date"
                  corners="rounded"
                  hasError={hasDealDateValidationError(errorFieldKeys, section.key, "end")}
                  tooltip={<KanaTooltip tooltipHeader="" tooltipText="Select the date this stage ends." />}
                />
              </Column>
              {section.keyDates.map((keyDate, i) => (
                <Column span="4_half" offset={i % 2 ? 1 : undefined} key={keyDate.key}>
                  <DatePicker
                    value={keyDate.value}
                    onChange={(value) => handleDateChange(section.key, keyDate.key, value)}
                    label={keyDate.label}
                    corners="rounded"
                    hasError={hasDealDateValidationError(errorFieldKeys, section.key, keyDate.key)}
                  />
                </Column>
              ))}
            </Row>
            {index < sections.length - 1 && (
              <Row spacingV="ll">
                <Column span={12}>
                  <Divider type="horizontal" />
                </Column>
              </Row>
            )}
          </div>
        ))}
        <div className="EditDealDatesPanelActions">
          <Button text="Save" type="submit" variant="primary" isLoading={isSaving} />
        </div>
      </form>
    </div>
  );

  return (
    <FlyoutPanel
      show={show}
      onClose={handleClose}
      size="Large"
      header="Edit stage dates"
      buttonsChildren={undefined}
      contentChildren={content}
    />
  );
};
